import { useEffect, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  useWatch,
} from 'react-hook-form';
import { ZodObject, ZodRawShape } from 'zod';
import { stepHasError } from './shared-form-stepper';
import { FormSubSteps, StepConfig } from './shared-toolbar-form-stepper';
import { getSchemaKeys } from './simple-form-page';

import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import StepIcon from '@mui/material/StepIcon';
import Check from '@mui/icons-material/Check';
import Lock from '@mui/icons-material/Lock';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import type { Theme } from '@mui/material/styles';

interface StepItemWrapperProps<T extends FieldValues> {
  step: StepConfig;
  index: number;
  activeStep: number;
  stepIsLocked: boolean[];
  errors: FieldErrors<T>;
  control: Control<T>;
  subSteps?: FormSubSteps[];
  theme: Theme;
  onStepClick: (index: number) => void;
  handleStepClick?: (index: number) => void;
  getValues: UseFormGetValues<T>;
}

export default function StepItemWrapper<T extends FieldValues>({
  step,
  index,
  activeStep,
  stepIsLocked,
  errors,
  control,
  subSteps,
  theme,
  onStepClick,
  handleStepClick,
  getValues,
}: StepItemWrapperProps<T>) {
  const fields = step.schema
    ? getSchemaKeys(step.schema as unknown as ZodObject<ZodRawShape>)
    : [];
  const currentValues = useWatch({
    control,
    name: fields as any,
  });

  const [debouncedValues, setDebouncedValues] = useState(currentValues);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValues(currentValues);
    }, 300);
    return () => clearTimeout(handler);
  }, [currentValues]);

  const isStepLocked = stepIsLocked[index];

  const isValid = useMemo(() => {
    if (isStepLocked || !step.schema) return true;

    return debouncedValues ? step.schema.safeParse(getValues()).success : false;
  }, [isStepLocked, step.schema, debouncedValues, getValues]);

  const status = isStepLocked ? 'locked' : isValid ? 'completed' : 'default';

  const hasError =
    step.schema &&
    !isStepLocked &&
    stepHasError(step.schema as ZodObject<ZodRawShape>, errors);

  const isStepCompleted = status === 'completed';
  const stepSubStep = subSteps?.filter((s) => s.stepIndex === step.label) ?? [];

  const stepColor = hasError
    ? theme.palette.semantic.stepper.warning
    : status === 'completed'
    ? theme.palette.semantic.stepper.completed
    : status === 'locked'
    ? theme.palette.semantic.stepper.locked
    : activeStep === index
    ? theme.palette.semantic.stepper.active
    : theme.palette.semantic.stepper.default;

  const stepIcon = isStepCompleted ? (
    <Check
      sx={{
        color: theme.palette.semantic.stepper.completed,
        fontSize: 'small',
      }}
    />
  ) : hasError ? (
    <WarningAmberOutlined
      sx={{ color: theme.palette.semantic.stepper.warning, fontSize: 'small' }}
    />
  ) : isStepLocked ? (
    <Lock
      sx={{ color: theme.palette.semantic.stepper.locked, fontSize: 'small' }}
    />
  ) : (
    <StepIcon icon={index + 1} />
  );

  return (
    <Step
      onClick={() =>
        isStepLocked
          ? undefined
          : !handleStepClick
          ? onStepClick(index)
          : handleStepClick(index)
      }
      disabled={isStepLocked}
      completed={isStepCompleted}
    >
      <StepLabel
        icon={stepIcon}
        sx={{
          '& .MuiStepLabel-label': {
            fontSize: 14,
            fontWeight: 400,
            letterSpacing: '0.17px',
            color: stepColor,
          },
          '& .MuiStepLabel-labelContainer': {
            color: stepColor,
          },
          '& .MuiStepLabel-label.Mui-active': {
            fontWeight: 400,
            color: stepColor,
          },
          '& .MuiStepIcon-root': {
            color: stepColor,
            opacity: 1,
          },
          '& .MuiStepIcon-root.Mui-active': {
            color: stepColor,
          },
          '& .MuiStepIcon-root.Mui-completed': {
            color: hasError
              ? theme.palette.semantic.stepper.warning
              : theme.palette.semantic.stepper.completed,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: theme.palette.semantic.stepper.completed,
          },
          cursor: isStepLocked ? 'default' : 'pointer',
          pointerEvents: isStepLocked ? 'none' : 'auto',
        }}
        error={hasError && isStepCompleted}
      >
        {step.label}
      </StepLabel>
      {stepSubStep && stepSubStep.length > 0 && (
        <StepContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {stepSubStep
              .slice()
              .reverse()
              .map((subDetail) => (
                <Typography key={subDetail.title} variant="body2">
                  {subDetail.title}
                </Typography>
              ))}
          </Stack>
        </StepContent>
      )}
    </Step>
  );
}
